"use client"

import { usePathname } from "next/navigation"
import { useEffect, useLayoutEffect, useCallback } from "react"

const scrollToTop = () => {
  window.scrollTo(0, 0)
  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = 0
  }
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

export const ScrollToTop = (): null => {
  const pathname = usePathname()

  const resetScroll = useCallback(() => {
    scrollToTop()
    requestAnimationFrame(scrollToTop)
    const t1 = setTimeout(scrollToTop, 50)
    const t2 = setTimeout(scrollToTop, 150)
    const t3 = setTimeout(scrollToTop, 300)
    const t4 = setTimeout(scrollToTop, 500)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
      clearTimeout(t4)
    }
  }, [])

  // React-level: reset on pathname change
  useLayoutEffect(() => {
    scrollToTop()
  }, [pathname])

  useEffect(() => {
    return resetScroll()
  }, [pathname, resetScroll])

  // Browser-level: intercept history.pushState directly for iOS Safari
  useEffect(() => {
    const originalPushState = history.pushState.bind(history)

    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      originalPushState(...args)
      resetScroll()
    }

    const onPopState = () => resetScroll()
    window.addEventListener("popstate", onPopState)

    return () => {
      history.pushState = originalPushState
      window.removeEventListener("popstate", onPopState)
    }
  }, [resetScroll])

  // Dynamically measure iOS Safari toolbar offset for ButtonDock
  useEffect(() => {
    const el = document.createElement("div")
    el.style.cssText = "position:fixed;top:0;height:100vh;width:0;pointer-events:none;visibility:hidden"
    document.body.appendChild(el)
    const vhPx = el.getBoundingClientRect().height
    document.body.removeChild(el)

    const update = () => {
      const offset = Math.max(0, vhPx - window.innerHeight)
      document.documentElement.style.setProperty("--dock-bottom", `${offset}px`)
    }

    update()
    window.visualViewport?.addEventListener("resize", update)
    window.addEventListener("resize", update)

    return () => {
      window.visualViewport?.removeEventListener("resize", update)
      window.removeEventListener("resize", update)
    }
  }, [])

  return null
}
